import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import './CreateCustomIndex.scss';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {ReactComponent as ProcessingIcon} from '../../../../assets/images/rebuild-processing-icon.svg';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrButton from '../../../common/arr-button/ArrButton';
import {useState} from 'react';
import {ArrStepper, Step} from '../../../common/arr-stepper/ArrStepper';
import {
  indexManagementStepperItems,
  STEP_KEY_BASIC_INFO,
  STEP_KEY_DOCUMENT_SELECTION,
  STEP_KEY_METADATA_FIELDS,
  STEP_KEY_REVIEW,
  STEP_KEY_SELECT_ANALYZERS,
} from './CreateCustomIndexUtils';
import DocumentSelectionCustomIndex from './document-selection/documentSelection';
import SelectAnalyzers from './select-analyzers/SelectAnalyzers';
import BasicInfo from './basic-info/BasicInfo';
import ArrModal from '../../../common/arr-modal/ArrModal';
import {ArrowBack} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {
  resetCustomIndexState,
  setTabValue,
} from '../../../../redux/slice/createCustomIndexSlice';
import {RootState} from '../../../../redux/store';
import ReviewStep from './review-step/ReviewStep';
import MetadataFields from './metadata-fields/MetadataFields';

export const CreateCustomIndex = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const requestBody =
    useSelector(
      (state: RootState) => state.indexManagement.createCustomIndex
    ) ?? [];
  const {createCustomIndex, indexStatus, isReadOnlyView} = useSelector(
    (state: RootState) => state.indexManagement
  );
  const selectedTreeItemId = useSelector(
    (state: RootState) => state.savedSearch?.selectedTreeItemId
  );
  const [steps, setSteps] = useState<Step[]>(indexManagementStepperItems());
  const [exitModalOpen, setExitModalOpen] = useState<boolean>(false);
  const [rebuildModalOpen, setRebuildModalOpen] = useState<boolean>(false);
  const currentIndex = steps.findIndex((s) => s.state === 'current');
  const goNext = () => {
    console.log('requestBody', requestBody);
    if (currentIndex !== steps.length - 1) {
      const nextIndex = currentIndex + 1;

      setSteps((prev) =>
        prev.map((step, i) =>
          i === currentIndex
            ? {...step, state: 'completed'}
            : i === nextIndex
              ? {...step, state: 'current'}
              : step
        )
      );
    } else {
      //if success it navigates here and shows a success toast
      dispatch(setTabValue(0));
      navigate('/Ingestion/Analytics');
      //if fails no navigation needed but shows a failure toast in the review step
    }
  };

  const goPrevious = () => {
    const prevIndex = currentIndex - 1;

    setSteps((prev) =>
      prev.map((step, i) =>
        i === currentIndex
          ? {...step, state: 'future'}
          : i === prevIndex
            ? {...step, state: 'current'}
            : step
      )
    );
  };
  const isDisabled = () => {
    const stepKey = steps[currentIndex]?.key;
    if (isReadOnlyView) {
      return false;
    }
    if (stepKey === STEP_KEY_BASIC_INFO && createCustomIndex?.IndexName) {
      return false;
    }
    if (stepKey === STEP_KEY_DOCUMENT_SELECTION && selectedTreeItemId) {
      return false;
    }
    if (
      stepKey === STEP_KEY_METADATA_FIELDS &&
      createCustomIndex?.Fields?.length > 0
    ) {
      return false;
    }
    if (stepKey === STEP_KEY_SELECT_ANALYZERS || stepKey === STEP_KEY_REVIEW) {
      return false;
    }
    return true;
  };

  const hideSubmitButton =
    isReadOnlyView && steps[currentIndex]?.key === STEP_KEY_REVIEW;
  const isRebuildIndexDisabled = !['Complete', 'Failed'].includes(indexStatus!);
  const isRemoveRebuildIndex = ['Standard Index1', 'Standard Index2'].includes(
    createCustomIndex.IndexName!
  );
  const rebuildModalDescription = (
    <>
      <Typography data-testid={`create-custom-index-description1`}>
        {t('indexManagement.customIndex.rebuildModal.description1')}
      </Typography>
      <Typography data-testid={`create-custom-index-typography`}>
        &nbsp;
      </Typography>
      <Typography data-testid={`create-custom-index-description2`}>
        {t('indexManagement.customIndex.rebuildModal.description2')}
      </Typography>
    </>
  );

  return (
    <Box className="custom-index">
      <Box className="custom-index-header">
        <Box className="header-title">
          {' '}
          <Typography
            className=" typo-text headline3_size"
            data-testid="custom-index-title"
          >
            {t('indexManagement.customIndex.title')}
          </Typography>
          {!isReadOnlyView && (
            <ArrTooltipWrapper
              title={t('ai.summarize.grid.excludedDocs')}
              trigger={
                <InfoIcon
                  fontSize="medium"
                  className="summarize-grid-info-icon"
                  data-testid="excluded-docs"
                />
              }
            >
              {t('indexManagement.customIndex.tooltipText')}
            </ArrTooltipWrapper>
          )}
        </Box>
        <Box className="custom-index-header-button-container">
          <ArrButton
            variant="secondary"
            size="medium"
            disabled={false}
            aria-label={t('indexManagement.customIndex.exit')}
            dataTestId="custom-index-exit"
            className="exit-button"
            onClick={() => {
              if (isReadOnlyView) {
                navigate('/Ingestion/Analytics');
                // add delay to clear the state
                setTimeout(() => {
                  dispatch(resetCustomIndexState());
                }, 200);
                return;
              }
              setExitModalOpen(true);
            }}
          >
            {t('indexManagement.customIndex.exit')}
          </ArrButton>
          {isReadOnlyView && !isRemoveRebuildIndex && (
            <ArrButton
              variant="primary"
              size="medium"
              disabled={isRebuildIndexDisabled}
              aria-label={t(
                'indexManagement.customIndex.readOnly.rebuildIndex'
              )}
              dataTestId="rebuild-index"
              startIcon={<ProcessingIcon />}
              onClick={() => {
                setRebuildModalOpen(true);
              }}
            >
              {t('indexManagement.customIndex.readOnly.rebuildIndex')}
            </ArrButton>
          )}
        </Box>
      </Box>
      <Box className="batch-print-stepper">
        <ArrStepper
          dataTestId={'create-custon-index-stepper'}
          steps={steps}
          currentStepKey={steps[currentIndex]?.key || ''}
          allowNonLinear={false}
          onStepChange={(key) => {
            setSteps((prev) =>
              prev.map((s) =>
                s.key === key
                  ? {...s, state: 'current'}
                  : s.state === 'current'
                    ? {...s, state: 'completed'}
                    : s
              )
            );
          }}
        />
      </Box>
      <Box className="stepper-button">
        <ArrButton
          variant="secondary"
          size="medium"
          onClick={goPrevious}
          disabled={false}
          aria-label={t('common.back')}
          startIcon={<ArrowBack />}
          className={currentIndex === 0 ? 'hide' : ''}
          dataTestId="custom-index-back"
        >
          {t('common.back')}
        </ArrButton>
        {!hideSubmitButton && (
          <ArrButton
            variant="primary"
            size="medium"
            onClick={goNext}
            className="next-button"
            disabled={isDisabled()}
            dataTestId="create-custom-next"
          >
            {currentIndex === steps.length - 1
              ? t('common.submit')
              : t('common.next')}
          </ArrButton>
        )}
      </Box>
      <Box>
        {steps[currentIndex]?.key == STEP_KEY_BASIC_INFO ? (
          <BasicInfo />
        ) : steps[currentIndex]?.key == STEP_KEY_DOCUMENT_SELECTION ? (
          <DocumentSelectionCustomIndex />
        ) : steps[currentIndex]?.key == STEP_KEY_METADATA_FIELDS ? (
          <MetadataFields />
        ) : steps[currentIndex]?.key == STEP_KEY_SELECT_ANALYZERS ? (
          <SelectAnalyzers />
        ) : steps[currentIndex]?.key == STEP_KEY_REVIEW ? (
          <>
            <ReviewStep />
          </>
        ) : (
          'Content for each step will go here.'
        )}
      </Box>
      {exitModalOpen && (
        <ArrModal
          showModal={exitModalOpen}
          showClose={false}
          onClose={() => {
            setExitModalOpen(false);
          }}
          title={t('common.areYouSure')}
          description={t('indexManagement.customIndex.basicInfo.unsavedText')}
          dataTestId="createcustomindex-modal"
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => {
                setExitModalOpen(false);
              },
              variant: 'secondary',
              dataTestId: 'cci-cancel-btn',
            },
            {
              id: '2',
              label: t('common.continue'),
              onClick: () => {
                navigate('/Ingestion/Analytics');
                dispatch(setTabValue(2));
              },
              variant: 'primary',
              dataTestId: 'cci-continue-btn',
            },
          ]}
        />
      )}
      {rebuildModalOpen && (
        <ArrModal
          showModal={rebuildModalOpen}
          showClose={false}
          dataTestId="custom-index-modal"
          onClose={() => {
            setRebuildModalOpen(false);
          }}
          title={t('indexManagement.customIndex.readOnly.rebuildIndex')}
          ariaLabel={
            t('indexManagement.customIndex.readOnly.rebuildIndex') + 'modal'
          }
          description={rebuildModalDescription}
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => {
                setRebuildModalOpen(false);
              },
              variant: 'secondary',
              ariaLabel: t('common.cancel'),
              dataTestId: 'rebuild-modal-cancel',
              ariaDescribedBy: t(
                'indexManagement.customIndex.rebuildModal.cancelBtnHint'
              ),
            },
            {
              id: '2',
              label: t('indexManagement.customIndex.readOnly.rebuildIndex'),
              onClick: () => {
                setRebuildModalOpen(false);
              },
              variant: 'primary',
              dataTestId: 'rebuild-modal-confirm',
              ariaLabel: t('indexManagement.customIndex.readOnly.rebuildIndex'),
              ariaDescribedBy: t(
                'indexManagement.customIndex.rebuildModal.rebuildBtnHint'
              ),
            },
          ]}
        />
      )}
    </Box>
  );
};
